import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/lib/AuthContext";
import api from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";
import Avatar, { invalidateAvatar } from "@/components/Avatar";
import { PageHeader, Card, Btn, Notice, PasswordField, PasswordRules, passwordRules, inputCls } from "@/dashboard/ui";
import { ShieldCheck, Upload, Trash2, KeyRound } from "lucide-react";
import BillingSettings from "@/dashboard/BillingSettings";
import BusinessProfileCard from "@/dashboard/BusinessProfileCard";

function ChangeEmailCard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [current, setCurrent] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user?.role !== "admin") return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(trimmed)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!current) {
      setError("Please enter your current password to confirm this change.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-email", { new_email: trimmed, current_password: current });
      logout();
      navigate("/login");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-6" data-testid="settings-email-card">
      <div className="mb-4 flex items-center gap-2.5">
        <ShieldCheck size={16} className="text-[#111111]" />
        <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Owner account email</p>
      </div>
      <p className="mb-3 text-xs text-neutral-500">
        Current sign-in: <span className="font-semibold text-neutral-700">{user?.email}</span>.
        Changing it updates your real login identity and signs you out everywhere — your role,
        data and permissions are preserved.
      </p>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label htmlFor="change-email-input" className="mb-1.5 block text-xs font-semibold text-neutral-600">New email address</label>
          <input
            id="change-email-input"
            data-testid="change-email-input"
            type="email"
            className={inputCls}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@savykernoai.com"
            autoComplete="email"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-neutral-600">Confirm with current password</label>
          <PasswordField testid="change-email-password-input" value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="Your current password" />
        </div>
        {error && <p role="alert" data-testid="change-email-error" className="rounded-xl bg-red-50 px-4 py-2.5 text-sm text-red-600">{error}</p>}
        <div className="flex justify-end">
          <Btn data-testid="change-email-submit" type="submit" disabled={busy}>
            {busy ? "Updating…" : "Change sign-in email"}
          </Btn>
        </div>
      </form>
    </Card>
  );
}


function ChangePasswordCard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!passwordRules.every((r) => r.test(next))) {
      setError("Please meet all the password requirements below.");
      return;
    }
    if (next !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      logout();
      navigate("/login");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-6" data-testid="settings-password-card">
      <div className="mb-4 flex items-center gap-2.5">
        <KeyRound size={16} className="text-[#111111]" />
        <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Change password</p>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-neutral-600">Current password</label>
          <PasswordField testid="current-password-input" value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="Your current password" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-neutral-600">New password</label>
          <PasswordField testid="new-password-input" value={next} onChange={(e) => setNext(e.target.value)} placeholder="14+ characters" />
        </div>
        <PasswordRules password={next} />
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-neutral-600">Confirm new password</label>
          <PasswordField testid="confirm-new-password-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat the new password" />
        </div>
        {error && <p data-testid="change-password-error" className="rounded-xl bg-red-50 px-4 py-2.5 text-sm text-red-600">{error}</p>}
        <p className="text-[11px] text-neutral-400">You'll be signed out everywhere after changing your password.</p>
        <Btn data-testid="change-password-button" type="submit" disabled={busy}>
          {busy ? "Updating…" : "Update Password"}
        </Btn>
      </form>
    </Card>
  );
}

const AVATAR_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_AVATAR_BYTES = 2 * 1024 * 1024;

function ProfilePictureCard() {
  const { user, refreshUser } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const pick = (e) => {
    const f = e.target.files?.[0];
    setError("");
    if (!f) {
      setFile(null);
      setPreview(null);
      return;
    }
    if (!AVATAR_TYPES.includes(f.type)) {
      setError("Please choose a PNG, JPG or WebP image.");
      setFile(null);
      setPreview(null);
      return;
    }
    if (f.size > MAX_AVATAR_BYTES) {
      setError("Profile picture must be under 2 MB.");
      setFile(null);
      setPreview(null);
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/profile/avatar", fd);
      invalidateAvatar(user?.avatar_url);
      await refreshUser();
      setFile(null);
      setPreview(null);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError("");
    try {
      await api.delete("/profile/avatar");
      invalidateAvatar(user?.avatar_url);
      await refreshUser();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-6" data-testid="settings-profile-picture-card">
      <p className="mb-1 text-xs font-bold uppercase tracking-wider text-neutral-500">Profile picture</p>
      <p className="mb-5 text-xs text-neutral-500">
        Only visible on your own account. PNG, JPG or WebP, up to 2 MB. This never changes the Savy Kerno brand logo.
      </p>
      <div className="flex items-center gap-5">
        <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border border-neutral-200 bg-neutral-50">
          {preview ? (
            <img src={preview} alt="Profile picture preview" className="h-full w-full object-cover" data-testid="avatar-preview" />
          ) : (
            <Avatar user={user} className="h-16 w-16 text-xl" testid="avatar-current" />
          )}
        </div>
        <div className="flex-1 space-y-2.5">
          <input
            data-testid="avatar-file-input"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={pick}
            className="block w-full text-xs text-neutral-500 file:mr-3 file:rounded-full file:border-0 file:bg-neutral-100 file:px-4 file:py-2 file:text-xs file:font-semibold file:text-[#111111] hover:file:bg-neutral-200"
          />
          <div className="flex gap-2">
            <Btn data-testid="avatar-upload-button" onClick={upload} disabled={!file || busy}>
              <Upload size={14} /> {busy ? "Saving…" : user?.avatar_url ? "Replace Picture" : "Upload Picture"}
            </Btn>
            {user?.avatar_url && (
              <Btn data-testid="avatar-remove-button" variant="ghost" onClick={remove} disabled={busy}>
                <Trash2 size={14} /> Remove
              </Btn>
            )}
          </div>
          {error && <p data-testid="avatar-error" className="text-xs text-red-600">{error}</p>}
          {!user?.avatar_url && !preview && (
            <p className="text-[11px] text-neutral-400">No picture yet — your initial is shown instead.</p>
          )}
        </div>
      </div>
    </Card>
  );
}

function BrandCard() {
  return (
    <Card className="p-6" data-testid="settings-brand-card">
      <p className="mb-1 text-xs font-bold uppercase tracking-wider text-neutral-500">Brand</p>
      <div className="flex items-center gap-4">
        <BrandLogo className="h-10 w-10" />
        <p className="text-xs leading-relaxed text-neutral-500">
          The official Savy Kerno logo is fixed across the website and dashboard and can't be changed per account.
        </p>
      </div>
    </Card>
  );
}

export default function Settings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" sub="Your account, branding and how Savy Kerno handles your data." />

      <div className="grid gap-4 lg:grid-cols-2">
        <BusinessProfileCard />

        <ProfilePictureCard />

        <BrandCard />

        <Card className="p-6" data-testid="settings-account-card">
          <p className="mb-4 text-xs font-bold uppercase tracking-wider text-neutral-500">Account</p>
          <div className="flex items-center gap-4">
            <Avatar user={user} className="h-14 w-14 text-lg" testid="account-avatar" />
            <div>
              <p className="text-base font-bold text-[#111111]">{user?.name}</p>
              <p className="text-sm text-neutral-500">{user?.email}</p>
              <p className="mt-0.5 text-xs text-neutral-400">Member since {String(user?.created_at || "").slice(0, 10)}</p>
            </div>
          </div>
          <div className="mt-6 border-t border-neutral-100 pt-5">
            <Btn
              data-testid="settings-sign-out-button"
              variant="ghost"
              onClick={() => {
                logout();
                navigate("/");
              }}
            >
              Sign out
            </Btn>
          </div>
        </Card>

        <ChangePasswordCard />

        <ChangeEmailCard />

        <BillingSettings />

        <Card className="p-6 lg:col-span-2" data-testid="settings-compliance-card">
          <div className="mb-4 flex items-center gap-2.5">
            <ShieldCheck size={16} className="text-[#111111]" />
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Security & compliance</p>
          </div>
          <ul className="space-y-2.5 text-sm text-neutral-600">
            <li>Your leads, agents, drafts and campaigns are visible only to your account.</li>
            <li>Phone numbers are validated with international country codes.</li>
            <li>Consent and do-not-call preferences are recorded per lead.</li>
            <li>Opted-out contacts are automatically excluded from calling campaigns.</li>
            <li>Automatic calling stays disabled until compliant telephony is in place.</li>
          </ul>
        </Card>
      </div>

      <Card className="mt-5 p-6" data-testid="settings-telephony-card">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="mb-1 text-xs font-bold uppercase tracking-wider text-neutral-500">Telephony — Phase C</p>
            <p className="text-sm font-semibold text-[#111111]">UK business number via Twilio</p>
            <ul className="mt-2 space-y-1 text-xs leading-relaxed text-neutral-500">
              <li>Local number: $3.50/month (~£2.80) · mobile: $2.50/month</li>
              <li>Inbound: $0.010/min · Outbound: $0.0158/min landline, $0.0305/min mobile</li>
              <li>AI speech and model usage applies per minute on top</li>
            </ul>
            <p className="mt-3 text-xs leading-relaxed text-neutral-500">
              To connect: create a Twilio account, then share your Account SID and Auth Token.
              A number is only provisioned after your explicit approval — nothing is charged before then.
            </p>
          </div>
          <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full border border-neutral-300 px-2.5 py-0.5 text-[11px] font-semibold text-neutral-500">
            Not connected
          </span>
        </div>
      </Card>

      <div className="mt-5">
        <Notice
          testid="settings-roadmap-notice"
          title="Coming in later phases"
          body="Live AI voice calls (LiveKit Agents / Pipecat), real email and SMS sending, social publishing and AI-assisted content writing. Nothing is billed or simulated in the meantime."
        />
      </div>
    </div>
  );
}
